import { type PropsWithChildren } from 'react';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import { motion } from 'framer-motion';
import { darken, lighten } from 'polished';
import styled from 'styled-components';

import {
  useNavDropdownContext,
  useNavDropdownTransform
} from './NavDropdownProvider';

const NavigationMenuViewport = styled(NavigationMenu.Viewport)`
  position: absolute;
  inset: 0;
`;

const NavigationMenuViewportPosition = styled.div`
  will-change: transform, height, width;
  position: absolute;
  border-radius: calc(5rem / 16);
  overflow: clip;

  box-shadow: ${({ theme }) => {
    const lowlight = `0px 0.5px 0 0.75px ${lighten(0.125, theme.palette.fg100)}`;
    const primary = '4px 16px 32px -12px hsla(0 0% 0% / 0.72)';
    return [lowlight, primary].join(',');
  }};

  &::before,
  &::after {
    pointer-events: none;
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
  }

  &::before {
    opacity: 0.98;
    background-image: ${({ theme }) => {
      const stop1 = theme.palette.fg100;
      const stop2 = darken(0.03125, theme.palette.fg100);
      return `radial-gradient(at 0% 0%, ${stop1} 58%, ${stop2})`;
    }};
  }

  &::after {
    pointer-events: none;
    content: '';
    position: absolute;

    border-radius: inherit;
    background-image: ${({ theme }) => theme.textures.noise.img};
    background-size: ${({ theme }) => theme.textures.noise.size};
    mix-blend-mode: ${({ theme }) => theme.textures.noise.mixBlendMode};
    opacity: ${({ theme }) => theme.textures.noise.opacity};
    opacity: 0;

    mask-image: radial-gradient(
      at 0% 0%,
      hsla(0 0% 100% / 1) 50%,
      hsla(0 0% 100% / 0.38)
    );

    z-index: 1;
  }
`;

const ViewportPosition = styled.div`
  position: absolute;
  width: 100%;
  top: calc(100% - 0.375rem);
  left: 0;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    display: none;
  }
`;

function NavMenuViewportWrapper({ children }: PropsWithChildren) {
  const state = useNavDropdownContext();
  const transformRaw = useNavDropdownTransform();

  const transform = transformRaw ? `translateX(${transformRaw}px)` : 'none';

  const optOutOfTransitionProps = state.shouldAnimateSize
    ? {}
    : { transition: { duration: 0 } };

  return (
    <ViewportPosition
      animate={state.value ? 'visible' : 'hidden'}
      as={motion.div}
      initial="hidden"
      variants={{
        visible: {
          opacity: 1,
          transform: 'translateY(0rem) '
          // transition: { type: 'spring', bounce: 0, duration: 0.28 }
        },
        hidden: {
          opacity: 0,
          transform: 'translateY(-0.5rem)'
          // transition: { type: 'spring', bounce: 0, duration: 0.2 }
        }
      }}>
      <NavigationMenuViewportPosition
        animate={{
          transform,
          height: state.boundingBoxes?.content.height || 0,
          width: state.boundingBoxes?.content.width || 0
        }}
        as={motion.div}
        style={{
          pointerEvents: state.value ? 'auto' : 'none'
        }}
        {...optOutOfTransitionProps}>
        {children}
      </NavigationMenuViewportPosition>
    </ViewportPosition>
  );
}

export function NavMenuViewport() {
  return (
    <NavMenuViewportWrapper>
      <NavigationMenuViewport forceMount />
    </NavMenuViewportWrapper>
  );
}
